<template>
  <div class="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 p-6 space-y-6 mb-6">
    <div
        v-if="isMobile || isTablet"
        class="order-2"
    >
      <div class="flex items-center justify-between gap-1">
        <button
            type="button"
            @click="redirectToIndex"
            class="inline-flex items-center py-2 hover-lift-no-shadow text-gray-700 hover:bg-gray-50 hover:rounded-md transition-colors"
            variant="ghost-no-padding"
        >
          <Icon name="arrow_back_ios" class="w-5 h-5  text-xl text-zinc-800" />
          {{ __('Back To Challenges') }}
        </button>
      </div>
      <div v-if="challenge.submission_points_earned !== null" class="gap-2 justify-center items-center flex mb-6 min-h-[3.5rem] px-2.5 py-4 bg-success/10 rounded-lg">
        <span class="text-2xl text-zinc-600">{{ __('Your score:')}}</span>
        <span class="text-2xl text-zinc-800 font-bold"> {{ challenge.submission_points_earned}}</span>
      </div>
      <div v-else-if="challenge.submission_needs_review" class="gap-2 justify-center items-center flex mb-6 min-h-[3.5rem] px-2.5 py-4 bg-[#FFF6C6] rounded-lg">
        <span class="text-2xl text-zinc-800">{{ __('In Review')}}</span>
      </div>
      <div class="mt-4">
        <h1 class="text-2xl md:text-3xl font-medium text-zinc-800">
          {{ challenge.title }}
        </h1>
      </div>
    </div>
    <div v-if="!isMobile">
      <div v-if="challenge.submission_points_earned !== null" class="gap-2 justify-center items-center flex mb-6 min-h-[3.5rem] px-2.5 py-4 bg-success/10 rounded-lg">
        <span class="text-2xl text-zinc-600">{{ __('Your score:')}}</span>
        <span class="text-2xl text-zinc-800 font-bold"> {{ challenge.submission_points_earned}}</span>
      </div>
      <div v-else-if="challenge.submission_needs_review" class="gap-2 justify-center items-center flex mb-6 min-h-[3.5rem] px-2.5 py-4 bg-[#FFF6C6] rounded-lg">
        <span class="text-2xl text-zinc-800">{{ __('In Review')}}</span>
      </div>
    </div>
    <div class="relative flex-shrink-0 w-full h-[150px] md:h-60 object-cover rounded-2xl overflow-hidden bg-gray-200 bg-cover bg-center bg-no-repeat"
         :style="previewImage"
    ></div>

    <div v-if="isMobile || isTablet" class="gap-6 space-y-6">
      <div>
        <span class="block w-full text-center px-3 py-2 text-gray-800 rounded-xl border-2 border-gray-200 text-2xl font-medium">
          {{ challenge.perfect_points }} {{ __('points') }}
        </span>
      </div>
      <div v-if="!challenge.submission_is_completed && !challenge.submission_needs_review" class="grid gap-4 grid-cols-2">
        <button
            v-if="!challenge.is_guest"
            type="button"
            @click="handleToggleSave"
            :class="[
              'px-3 py-2 rounded-md border-2 transition-colors flex items-center justify-center gap-1 col-span-1',
              isSaved
                ? 'bg-green-500 text-white hover:bg-green-600 border-green-500'
                : 'bg-none text-primary hover:bg-primary/10 border-primary'
            ]"
        >
          <Icon
              :name="isSaved ? 'playlist_add_check' : 'playlist_add'"
              class="text-2xl"
              :class="isSaved ? 'text-white' : 'text-primary'"
          />
          <span class="text-sm md:text-base">{{ saving ? __('Saving...') : (isSaved ? __('Saved') : __('Save For Later')) }}</span>
        </button>
        <button
            v-if="challenge.is_saved_for_later || challenge.has_attempted"
            type="button"
            @click="handleContinueChallenge()"
            class="px-4 py-2 rounded-md transition-colors bg-primary text-white hover:bg-primary/90 col-span-1"
        >
          <span class="text-sm md:text-base whitespace-nowrap">{{ __('Continue Challenge') }}</span>
        </button>
        <button
            v-else
            type="button"
            @click="challenge.show_instructions ? goToInstructionsPage(name, challenge) : handleStartChallenge()"
            class="px-4 py-2 rounded-md transition-colors bg-primary text-white hover:bg-primary/90 col-span-1"
        >
          <span class="text-sm md:text-base whitespace-nowrap">{{ __('Start Challenge') }}</span>
        </button>
      </div>
      <div v-else-if="challenge.submission_points_earned !== null" class="w-full">
        <button
            type="button"
            @click="viewResults(challenge)"
            class="w-full px-6 py-2 rounded-md transition-colors bg-primary text-white hover:bg-primary/90"
        >
          <span class="text-base">{{ __('View Results') }}</span>
        </button>
      </div>
      <div v-if="isDesktop">
        <span class="block w-full text-center px-3 py-2 text-gray-800 rounded-xl border-2 border-gray-200 text-2xl font-medium">
          {{ challenge.perfect_points }} {{ __('points') }}
        </span>
      </div>
    </div>
    <div
        v-if="isDesktop"
        class="flex items-center justify-between">
      <h1 class="text-3xl font-medium text-zinc-800">
        {{ challenge.title }}
      </h1>
      <span class="px-3 py-1  text-gray-800 rounded-xl border-2 border-gray-200 text-2xl font-medium">
        {{ challenge.perfect_points }} {{ __('points') }}
      </span>
    </div>
    <div class="space-y-6 rounded-xl border-2 border-gray-200 p-6">
      <div class="flex flex-wrap gap-2">
        <span class="px-3 py-1  text-zinc-800 rounded-full border-2 border-gray-200 text-sm md:font-medium">
          {{ challenge.type_label }}
        </span>
        <span v-if="challenge.category" class="px-3 py-1  text-zinc-800 rounded-full border-2 border-gray-200 text-sm md:font-medium">
          {{ challenge.category }}
        </span>
        <span
            class="inline-flex items-center px-3 py-1 text-zinc-800 rounded-full border-2 border-gray-200 text-sm md:font-medium"
        >
          <img
              class="rounded-sm object-cover w-5 h-5 mr-1"
              :src="companyLogo"
              :alt="__('Company logo image')"
          />
          {{ challenge.company.name }}
        </span>
      </div>

      <p class="text-gray-600 text-sm leading-relaxed whitespace-pre-line">
        {{ challenge.description || __('No description provided') }}
      </p>

      <YoutubePlayer
          v-if="challenge.media_type === 2 && challenge.provider === 'youtube'"
          :videoId="challenge.video_id" />

      <CloudflarePlayer
          v-if="challenge.media_type === 2 && challenge.provider === 'cloudflare'"
          :videoId="challenge.video_id" />

      <img
          v-if="challenge.media_type === 1"
          class="h-72 md:h-[344px] w-full md:w-[615px] mx-auto overflow-hidden rounded-2xl"
          :src="contentMediaImage"
          :alt="__('Challenge preview image')"
      />

      <div v-if="challenge.media_type === 3">
        <span> {{ __('Follow the link here to ')}} </span>
        <a :href="challenge.media_content" target="_blank" class="text-primary underline">
          {{ __('View Challenge Details.') }}
        </a>
      </div>
      <div>
        <ExpirationBar
            :start-date="challenge.start_at"
            :end-date="challenge.end_at"
            :never-expires="challenge.never_expires"
        />
      </div>
    </div>
    <div
        v-if="isDesktop"
        class="flex justify-between mt-8"
    >
      <button
          type="button"
          @click="redirectToIndex"
          class="inline-flex items-center px-6 py-2 hover-lift-no-shadow text-gray-700 hover:bg-gray-50 hover:rounded-md transition-colors"
          variant="ghost-no-padding"
      >
        <Icon name="arrow_back_ios" class="w-5 h-5  text-xl text-zinc-800" />
        {{ __('Back To Challenges') }}
      </button>
      <div v-if="!challenge.submission_is_completed && !challenge.submission_needs_review" class="ml-auto flex gap-3">
        <button
            v-if="!challenge.is_guest"
            type="button"
            @click="handleToggleSave"
            :class="[
              'px-6 py-2 rounded-md border-2 transition-colors flex items-center gap-2',
              isSaved
                ? 'bg-green-500 text-white hover:bg-green-600 border-green-500'
                : 'bg-none text-primary hover:bg-primary/10 border-primary'
            ]"
        >
          <Icon
              :name="isSaved ? 'playlist_add_check' : 'playlist_add'"
              class="text-2xl"
              :class="isSaved ? 'text-white' : 'text-primary'"
          />
          <span class="text-base">{{ saving ? __('Saving...') : (isSaved ? __('Saved') : __('Save For Later')) }}</span>
        </button>
        <button
            v-if="challenge.is_saved_for_later || challenge.has_attempted"
            type="button"
            @click="handleContinueChallenge()"
            class="px-6 py-2 rounded-md transition-colors bg-primary text-white hover:bg-primary/90"
        >
          <span class="text-base">{{ __('Continue Challenge') }}</span>
        </button>
        <button
            v-else
            type="button"
            @click="challenge.show_instructions ? goToInstructionsPage(name, challenge) : handleStartChallenge()"
            class="px-6 py-2 rounded-md transition-colors bg-primary text-white hover:bg-primary/90"
        >
          <span class="text-base">{{ __('Start Challenge') }}</span>
        </button>
      </div>
      <div v-else-if="challenge.submission_points_earned !== null">
        <button
            type="button"
            @click="viewResults(challenge)"
            class="px-6 py-2 rounded-md transition-colors bg-primary text-white hover:bg-primary/90"
        >
          <span class="text-base">{{ __('View Results') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref} from 'vue';
import ExpirationBar from '@/pages/company/challenges/components/ExpirationBar.vue';
import { __ } from '@/helpers';
import {Icon} from "@/components/block/icon";
import axios from "axios";
import {useToast} from "vue-toastification";
import YoutubePlayer from "@/pages/candidate/challenges/components/YoutubePlayer.vue";
import CloudflarePlayer from "@/pages/candidate/challenges/components/CloudflarePlayer.vue";

const props = defineProps({
  challenge: {
    type: Object,
    required: true
  },
  name: {
    type: String,
  },
  isSaved: {
    type: Boolean,
    default: false
  },
  savedAttempt: {
    type: Number,
    default: null
  }
});

const saving = ref(false);
const toast = useToast();
const isSaved = ref(props.challenge.is_saved || false);
const windowWidth = ref(window.innerWidth);

const isMobile = computed(() => windowWidth.value < 640)
const isTablet = computed(() => windowWidth.value >= 640 && windowWidth.value < 1024)
const isDesktop = computed(() => windowWidth.value >= 1024)

const svgPositionMap = computed(() => {
  if (isMobile.value) {
    return {
      'object-1.svg': { size: '65%', position: '65% 110%' },
      'object-2.svg': { size: '90%', position: '-100% -70%' },
      'object-3.svg': { size: '55%', position: '70% -200%' },
      'object-4.svg': { size: '80%', position: '35% 0%' },
      'object-5.svg': { size: '60%', position: '0% 60%' },
      'object-6.svg': { size: '70%', position: '30% 0%' },
      'object-7.svg': { size: '50%', position: '100% 60%' },
      'object-8.svg': { size: '55%', position: '25% 310%' },
      'default': { size: '70%', position: '50% 10%' }
    };
  }

  return {
    'object-1.svg': { size: '60%', position: '80% 140%' },
    'object-2.svg': { size: '68%', position: '80% -90%' },
    'object-3.svg': { size: '48%', position: '80% -10%' },
    'object-4.svg': { size: '45%', position: '80% 5%' },
    'object-5.svg': { size: '40%', position: '80% 50%' },
    'object-6.svg': { size: '37%', position: '80% 0%' },
    'object-7.svg': { size: '40%', position: '100% 50%' },
    'object-8.svg': { size: '48%', position: '80% 115%' },
    'default': { size: '50%', position: '80% 0%' }
  };
});

const previewImage = computed(() => {
  const url = props.challenge.preview_image_url;
  const isSvg = props.challenge.is_random_preview;

  if (isSvg) {
    const svgName = props.challenge.preview_image
    const svgProperties = svgPositionMap.value[svgName] || svgPositionMap.value.default;

    return {
      backgroundImage: `url('${url}')`,
      backgroundSize: svgProperties.size,
      backgroundPosition: svgProperties.position,
      backgroundColor: 'black',
    }
  }
  return {
    backgroundImage: `url('${props.challenge.preview_image_url}')`,
    backgroundSize: 'cover',
    backgroundPosition: 'center'
  }

});

const contentMediaImage = computed(() => {
  return props.challenge.media_content
      ? `/uploads/${props.challenge.media_content}`
      : '/images/placeholder_challenge.png';
});

const companyLogo = computed(() => {
  const logo = props.challenge.company.logo;
  return logo
      ? `/uploads/${logo}`
      : '/images/logo_placeholder.png';
});

const handleResize = () => {
  windowWidth.value = window.innerWidth;
}

onMounted(() => {
  window.addEventListener("resize", handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize)
})

function redirectToIndex() {
  window.location.href = route('challenges.index');
}

const viewResults = (challenge) => {
  window.location.href = route('redesign.candidate.challenges.results', {
    challenge: challenge.id,
    attempt: challenge.submission_id
  });
};

const handleToggleSave = async () => {
  if (isSaved.value) {
    await handleUnsave();
  } else {
    await handleSave();
  }
};

const handleSave = async () => {
  saving.value = true;
  try {
    const response = await axios.post(route('redesign.candidate.challenges.save', {
      challenge: props.challenge.id
    }));

    toast.success(response.data.message || __('Challenge saved successfully'));
    isSaved.value = true;
  } catch(error) {
    toast.error(
        error.response?.data?.message || __('Error saving the challenge')
    );
  } finally {
    saving.value = false;
  }
};

const handleUnsave = async () => {
  saving.value = true;
  try {
    const response = await axios.delete(route('redesign.candidate.challenges.unsave', {
      challenge: props.challenge.id
    }));

    toast.success(response.data.message || __('Challenge removed from saved list'));
    isSaved.value = false;
  } catch(error) {
    toast.error(
        error.response?.data?.message || __('Error removing saved challenge')
    );
  } finally {
    saving.value = false;
  }
};

const handleContinueChallenge = () => {
  window.location.href = route('redesign.candidate.challenges.resume', {
    challenge: props.challenge.id
  });
};

const handleStartChallenge = async () => {
   if (props.challenge.is_guest) {
     sessionStorage.setItem('pending_challenge_start', props.challenge.id);
     window.location.href = `/login?redirect=${encodeURIComponent(window.location.href)}&action=start`;
     return;
   }
   try {
    const response = await axios.post(route('redesign.candidate.challenges.start', {
      challenge: props.challenge.id
    }));

    if (response.data.redirect) {
      window.location.href = response.data.redirect;
    }
  } catch (error) {
    toast.error(error.response?.data?.message || __('Failed to start challenge'));
  }
};

async function goToInstructionsPage(name, challenge) {
  await axios.post(route('redesign.candidate.challenges.store-session'), { name })

  window.location.href = route('redesign.candidate.challenges.instructions', { challenge: challenge.id });
}
</script>
